'use client';

import type { JSX, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@calm-web/design-system';

import FullscreenBackground from '@/components/image/FullscreenBackground';
import FullscreenOverlay from '@/components/image/FullscreenOverlay';

import messages from './messages';
import { Body, ButtonPadding, Container, Grid, Head, Wrapper } from './styles';

interface NotFoundContentProps {
	// Pages Router renders <Header /> inside the Wrapper; the App Router boundaries
	// have no Header (and no Redux store to back one), so this is optional.
	header?: ReactNode;
}

/**
 * Redux-free presentational core shared by the Pages Router 404 and the App
 * Router not-found/unauthorized/forbidden boundaries. The App Router tree has
 * no store, so this must not depend on analytics or store-backed hooks.
 *
 * `Wrapper` (styled-components) lives here rather than in the App Router files
 * themselves: those are Server Components, and evaluating a styled-components
 * module directly in the RSC build environment crashes (`createContext is not
 * a function`). Keeping it behind this `'use client'` boundary avoids that.
 */
const NotFoundContent = ({ header }: NotFoundContentProps): JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Wrapper>
			{header}
			<FullscreenBackground src="/_n/images/jasper-lake-hi.webp" />
			<FullscreenOverlay gradient="gradientHome" mobileGradient="gradientHomeMobile" />
			<Grid columns={2}>
				<Container>
					<Head>{formatMessage(messages.title)}</Head>
					<Body>{formatMessage(messages.message1)}</Body>
					<Body>{formatMessage(messages.message2)}</Body>
					<ButtonPadding>
						<Button textColor="gray6" backgroundColor="white" href="/">
							{formatMessage(messages.cta)}
						</Button>
					</ButtonPadding>
				</Container>
			</Grid>
		</Wrapper>
	);
};

export default NotFoundContent;
